/**
 * Image context types for optimized responsive image generation
 * Each context generates only the sizes it actually needs
 */

export type ImageContext =
  | 'thumbnail'  // Small previews, list items
  | 'card'       // Card media, grid items
  | 'content'    // Inline content images
  | 'question'   // Question box selection images
  | 'logo'       // Brand logos, banners
  | 'feature'    // Feature block images
  | 'hero'       // Hero sections, full-width headers
  | 'background'; // CSS background images

/**
 * Configuration for image sizes based on context
 * Each context specifies which sizes to generate
 */
export const IMAGE_CONTEXT_SIZES: Record<ImageContext, number[]> = {
  thumbnail: [200, 400],           // Small previews never need large sizes
  card: [200, 400, 600],          // Cards max out at ~400px on desktop
  content: [400, 600, 800],       // Body content images
  question: [200, 400],           // Small selection boxes
  logo: [200, 400],              // Usually small, fixed size
  feature: [400, 600, 800],      // Medium-sized feature blocks
  hero: [800, 1200, 1440],       // Full-width hero sections
  background: [600, 1200, 1440]  // CSS backgrounds need to cover viewport
};

/**
 * Default sizes attribute for each context
 * Used for responsive image selection hints
 */
export const IMAGE_CONTEXT_SIZES_ATTR: Record<ImageContext, string> = {
  thumbnail: '(min-width: 768px) 200px, 100vw',
  card: '(min-width: 768px) 400px, 100vw',
  content: '(min-width: 768px) 800px, 100vw',
  question: '(min-width: 768px) 200px, 100vw',
  logo: '200px',
  feature: '(min-width: 1200px) 800px, (min-width: 768px) 600px, 100vw',
  hero: '(min-width: 1440px) 1440px, (min-width: 768px) 1200px, 100vw',
  background: '100vw'
};

/**
 * Get the appropriate suffix for a given width
 */
export function getImageSuffix(width: number): string {
  return `_w${width}`;
}

/**
 * Get the default size for a context (used when no srcset is available)
 */
export function getDefaultSizeForContext(context: ImageContext): number {
  const sizes = IMAGE_CONTEXT_SIZES[context];
  // Return the middle size or the largest if only 2 sizes
  return sizes.length > 2 ? sizes[1] : sizes[sizes.length - 1];
}